import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Settings2, Sliders, Check, Shield, ArrowLeft, GraduationCap, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fetchAIUsage } from "@/services/writingCorrectionService";
import { getOrCreateClientId } from "@/utils/clientId";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [readingTime, setReadingTime] = useState(60);
  const [listeningTime, setListeningTime] = useState(60);
  const [writingTime, setWritingTime] = useState(60);
  const [shuffle, setShuffle] = useState(true);
  const [audioLimit, setAudioLimit] = useState("2"); // Play count limits
  
  const [saved, setSaved] = useState(false);

  // User Goals States
  const [goalMode, setGoalMode] = useState<"same" | "individual">("same");
  const [globalNclcGoal, setGlobalNclcGoal] = useState("7");
  const [readingGoal, setReadingGoal] = useState("7");
  const [listeningGoal, setListeningGoal] = useState("7");
  const [writingGoal, setWritingGoal] = useState("7");
  const [goalSaved, setGoalSaved] = useState(false);

  // AI Stats State
  const [aiStats, setAiStats] = useState<{
    usedToday: number;
    dailyLimit: number;
    remaining: number;
    totalCorrections?: number;
    lastCorrectionAt?: string | null;
    success?: boolean;
  } | null>(null);

  useEffect(() => {
    // Load config simulation
    const config = localStorage.getItem("tef_trainer_settings");
    if (config) {
      const parsed = JSON.parse(config);
      setReadingTime(parsed.readingTime || 60);
      setListeningTime(parsed.listeningTime || 60);
      setWritingTime(parsed.writingTime || 60);
      setShuffle(parsed.shuffle !== undefined ? parsed.shuffle : true);
      setAudioLimit(parsed.audioLimit || "2");
    }

    // Load user goals
    const savedGoals = localStorage.getItem("tef_user_goals");
    if (savedGoals) {
      try {
        const parsed = JSON.parse(savedGoals);
        setGoalMode(parsed.mode || "same");
        setGlobalNclcGoal(parsed.globalNclcGoal || "7");
        if (parsed.goals) {
          setReadingGoal(parsed.goals.reading || "7");
          setListeningGoal(parsed.goals.listening || "7");
          setWritingGoal(parsed.goals.writing || "7");
        }
      } catch (err) {
        console.error("Error parsing user goals in Settings page", err);
      }
    }

    // Load AI Usage statistics
    const loadAiStats = async () => {
      const clientId = getOrCreateClientId();
      const stats = await fetchAIUsage(clientId);
      if (stats.success) {
        setAiStats(stats);
      } else {
        setAiStats(null);
      }
    };
    loadAiStats();
  }, []);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const config = {
      readingTime,
      listeningTime,
      writingTime,
      shuffle,
      audioLimit
    };
    localStorage.setItem("tef_trainer_settings", JSON.stringify(config));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveGoals = (e: React.MouseEvent) => {
    e.preventDefault();
    const goalsObj = {
      mode: goalMode,
      globalNclcGoal,
      goals: {
        reading: goalMode === "same" ? globalNclcGoal : readingGoal,
        listening: goalMode === "same" ? globalNclcGoal : listeningGoal,
        writing: goalMode === "same" ? globalNclcGoal : writingGoal
      },
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem("tef_user_goals", JSON.stringify(goalsObj));
    setGoalSaved(true);
    setTimeout(() => setGoalSaved(false), 2000);
    
    // Broadcast the updated event
    window.dispatchEvent(new Event("dashboardUpdated"));
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto animate-in fade-in duration-500 pb-12">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("common.back", "Voltar")}
      </Button>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("sidebar.settings", "Configurações")}</h1>
        <p className="text-gray-500 mt-1">{t("settings.description", "Configure as diretrizes padrões de cronômetro, mídias e embaralhamento para seus simulados.")}</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <Card className="border-none shadow-sm bg-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sliders className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t("settings.timerTitle", "Tempo de Prova Padrão (Minutos)")}</CardTitle>
            </div>
            <CardDescription>{t("settings.timerDesc", "Ajuste os tempos limites regulamentares para cada módulo específico.")}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="readingTime">{t("settings.readingLabel", "Reading (Lectures)")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="readingTime"
                  type="number"
                  min="5"
                  max="120"
                  value={readingTime}
                  onChange={e => setReadingTime(parseInt(e.target.value) || 60)}
                  className="bg-gray-50 border-none rounded-xl text-center font-bold"
                />
                <span className="text-xs text-gray-400 font-semibold">{t("settings.minUnit", "min")}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="listeningTime">{t("settings.listeningLabel", "Listening (Écoutes)")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="listeningTime"
                  type="number"
                  min="5"
                  max="120"
                  value={listeningTime}
                  onChange={e => setListeningTime(parseInt(e.target.value) || 60)}
                  className="bg-gray-50 border-none rounded-xl text-center font-bold"
                />
                <span className="text-xs text-gray-400 font-semibold">{t("settings.minUnit", "min")}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="writingTime">{t("settings.writingLabel", "Writing (Écritures)")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="writingTime"
                  type="number"
                  min="5"
                  max="120"
                  value={writingTime}
                  onChange={e => setWritingTime(parseInt(e.target.value) || 60)}
                  className="bg-gray-50 border-none rounded-xl text-center font-bold"
                />
                <span className="text-xs text-gray-400 font-semibold">{t("settings.minUnit", "min")}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Engine parameter configurations */}
        <Card className="border-none shadow-sm bg-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t("settings.engineRulesTitle", "Regras do Motor de Prova")}</CardTitle>
            </div>
            <CardDescription>{t("settings.engineRulesDesc", "Gerencie o comportamento das questões e mídias.")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Shuffling options toggle wrapper */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="space-y-0.5">
                <Label className="font-bold text-gray-800 text-sm">{t("settings.shuffleLabel", "Embaralhar Questões")}</Label>
                <p className="text-xs text-gray-500">{t("settings.shuffleDesc", "Alterna as ordens das questões e alternativas para evitar fixação viciada.")}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={shuffle} 
                  onChange={(e) => setShuffle(e.target.checked)}
                  className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Audio plays limits */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="space-y-0.5">
                <Label htmlFor="audioLimit" className="font-bold text-gray-800 text-sm">{t("settings.audioLimitLabel", "Limite de Reproduções de Áudio")}</Label>
                <p className="text-xs text-gray-500">{t("settings.audioLimitDesc", "Número máximo permitido de audições para a competência Listening.")}</p>
              </div>
              <Select value={audioLimit} onValueChange={setAudioLimit}>
                <SelectTrigger id="audioLimit" className="bg-white border-gray-200 rounded-xl w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("settings.audioPlays_1", "1 reprodução")}</SelectItem>
                  <SelectItem value="2">{t("settings.audioPlays_2", "2 reproduções")}</SelectItem>
                  <SelectItem value="unlimited">{t("settings.audioPlays_unlimited", "Ilimitado")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Meta Pessoal TEF / NCLC */}
        <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t("settings.goalTitle", "Meta pessoal TEF / NCLC")}</CardTitle>
            </div>
            <CardDescription>
              {t("settings.goalDesc", "Defina seu objetivo pessoal para acompanhar quantos pontos faltam até o nível desejado em cada competência.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="font-bold text-gray-800 text-sm">{t("settings.goalDefLabel", "Definição da Meta")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                <Button
                  type="button"
                  variant={goalMode === "same" ? "default" : "outline"}
                  onClick={() => setGoalMode("same")}
                  className={`rounded-xl min-h-[2.75rem] h-auto py-2 font-bold whitespace-normal break-words text-center text-xs sm:text-sm leading-snug w-full ${
                    goalMode === "same" ? "bg-primary text-white" : "border border-gray-200 text-gray-700"
                  }`}
                >
                  <span className="sm:hidden">{t("settings.goalSameShort", "Mesma meta")}</span>
                  <span className="hidden sm:inline">{t("settings.goalSame", "Mesma meta para todas")}</span>
                </Button>
                <Button
                  type="button"
                  variant={goalMode === "individual" ? "default" : "outline"}
                  onClick={() => setGoalMode("individual")}
                  className={`rounded-xl min-h-[2.75rem] h-auto py-2 font-bold whitespace-normal break-words text-center text-xs sm:text-sm leading-snug w-full ${
                    goalMode === "individual" ? "bg-primary text-white" : "border border-gray-200 text-gray-700"
                  }`}
                >
                  {t("settings.goalIndividual", "Meta individual")}
                </Button>
              </div>
            </div>

            {goalMode === "same" ? (
              <div className="space-y-1.5 max-w-sm pt-2">
                <Label htmlFor="globalGoal">{t("settings.goalNclcSelect", "Meta NCLC desejada")}</Label>
                <Select value={globalNclcGoal} onValueChange={setGlobalNclcGoal}>
                  <SelectTrigger id="globalGoal" className="bg-gray-50 border-none rounded-xl h-11">
                    <SelectValue placeholder={t("settings.goalNclcSelect", "Selecione a meta NCLC")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">NCLC 4</SelectItem>
                    <SelectItem value="5">NCLC 5</SelectItem>
                    <SelectItem value="6">NCLC 6</SelectItem>
                    <SelectItem value="7">{t("dashboard.defaultNclcGoalOption", "NCLC 7 (Padrão)")}</SelectItem>
                    <SelectItem value="8">NCLC 8</SelectItem>
                    <SelectItem value="9">NCLC 9</SelectItem>
                    <SelectItem value="10+">NCLC 10+</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-400 font-semibold italic">{t("settings.goalDefaultNote", "Nota: NCLC 7 é a meta padrão caso não alterado.")}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3 pt-2">
                <div className="space-y-1.5">
                  <Label htmlFor="readingGoal">{t("settings.readingGoal", "Meta Compréhension Écrite (CE)")}</Label>
                  <Select value={readingGoal} onValueChange={setReadingGoal}>
                    <SelectTrigger id="readingGoal" className="bg-gray-50 border-none rounded-xl h-11">
                      <SelectValue placeholder={t("settings.readingGoalPlaceholder", "Meta CE")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">NCLC 4</SelectItem>
                      <SelectItem value="5">NCLC 5</SelectItem>
                      <SelectItem value="6">NCLC 6</SelectItem>
                      <SelectItem value="7">NCLC 7</SelectItem>
                      <SelectItem value="8">NCLC 8</SelectItem>
                      <SelectItem value="9">NCLC 9</SelectItem>
                      <SelectItem value="10+">NCLC 10+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="listeningGoal">{t("settings.listeningGoal", "Meta Compréhension Orale (CO)")}</Label>
                  <Select value={listeningGoal} onValueChange={setListeningGoal}>
                    <SelectTrigger id="listeningGoal" className="bg-gray-50 border-none rounded-xl h-11">
                      <SelectValue placeholder={t("settings.listeningGoalPlaceholder", "Meta CO")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">NCLC 4</SelectItem>
                      <SelectItem value="5">NCLC 5</SelectItem>
                      <SelectItem value="6">NCLC 6</SelectItem>
                      <SelectItem value="7">NCLC 7</SelectItem>
                      <SelectItem value="8">NCLC 8</SelectItem>
                      <SelectItem value="9">NCLC 9</SelectItem>
                      <SelectItem value="10+">NCLC 10+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="writingGoal">{t("settings.writingGoal", "Meta Expression Écrite (EE)")}</Label>
                  <Select value={writingGoal} onValueChange={setWritingGoal}>
                    <SelectTrigger id="writingGoal" className="bg-gray-50 border-none rounded-xl h-11">
                      <SelectValue placeholder={t("settings.writingGoalPlaceholder", "Meta EE")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">NCLC 4</SelectItem>
                      <SelectItem value="5">NCLC 5</SelectItem>
                      <SelectItem value="6">NCLC 6</SelectItem>
                      <SelectItem value="7">NCLC 7</SelectItem>
                      <SelectItem value="8">NCLC 8</SelectItem>
                      <SelectItem value="9">NCLC 9</SelectItem>
                      <SelectItem value="10+">NCLC 10+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end border-t border-gray-50 bg-gray-50/50 p-4 rounded-b-2xl">
            <Button
              type="button"
              onClick={handleSaveGoals}
              className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl h-10 px-5 shadow-sm flex items-center gap-2 text-xs"
            >
              {goalSaved ? (
                <>
                  <Check className="w-4 h-4" /> {t("settings.goalSavedSuccess", "Meta pessoal atualizada com sucesso!")}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> {t("settings.saveGoal", "Salvar Meta")}
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Tabela de Referência Oficial TEF / NCLC */}
        <Card className="border-none shadow-sm bg-white overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t("settings.conversionTableTitle", "Tabela de Conversão Oficial TEF / NCLC / CECRL")}</CardTitle>
            </div>
            <CardDescription>{t("settings.conversionTableDesc", "Consulte a correspondência de pontuação oficial usada para fins de imigração para o Canadá.")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="p-3">{t("settings.nclcLevel", "Nível NCLC")}</th>
                    <th className="p-3">{t("settings.cefrLevel", "Nível CECRL")}</th>
                    <th className="p-3">{t("settings.ceScore", "Pontuação CE (Leitura)")}</th>
                    <th className="p-3">{t("settings.coScore", "Pontuação CO (Escuta)")}</th>
                    <th className="p-3">{t("settings.eeScore", "Pontuação EE (Escrita)")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 10+</td>
                    <td className="p-3"><Badge className="bg-indigo-50 text-indigo-700 border-none">C2</Badge></td>
                    <td className="p-3">549 - 699</td>
                    <td className="p-3">549 - 699</td>
                    <td className="p-3">416 - 450</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 9</td>
                    <td className="p-3"><Badge className="bg-indigo-50 text-indigo-700 border-none">C1</Badge></td>
                    <td className="p-3">500 - 548</td>
                    <td className="p-3">500 - 548</td>
                    <td className="p-3">371 - 415</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 8</td>
                    <td className="p-3"><Badge className="bg-blue-50 text-blue-700 border-none">B2</Badge></td>
                    <td className="p-3">453 - 499</td>
                    <td className="p-3">453 - 499</td>
                    <td className="p-3">349 - 370</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 7</td>
                    <td className="p-3"><Badge className="bg-blue-50 text-blue-700 border-none">B2</Badge></td>
                    <td className="p-3">406 - 452</td>
                    <td className="p-3">406 - 452</td>
                    <td className="p-3">310 - 348</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 6</td>
                    <td className="p-3"><Badge className="bg-purple-50 text-purple-700 border-none">B1</Badge></td>
                    <td className="p-3">361 - 405</td>
                    <td className="p-3">361 - 405</td>
                    <td className="p-3">271 - 309</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 5</td>
                    <td className="p-3"><Badge className="bg-purple-50 text-purple-700 border-none">B1</Badge></td>
                    <td className="p-3">317 - 360</td>
                    <td className="p-3">317 - 360</td>
                    <td className="p-3">226 - 270</td>
                  </tr>
                  <tr className="hover:bg-gray-50/50">
                    <td className="p-3 font-bold text-primary">NCLC 4</td>
                    <td className="p-3"><Badge className="bg-gray-50 text-gray-600 border-none">A2</Badge></td>
                    <td className="p-3">233 - 316</td>
                    <td className="p-3">280 - 316</td>
                    <td className="p-3">181 - 225</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Uso de Inteligência Artificial */}
        <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <CardTitle className="text-lg">{t("settings.aiUsageTitle", "Uso de Inteligência Artificial")}</CardTitle>
            </div>
            <CardDescription>
              {t("settings.aiUsageDesc", "Acompanhe suas estatísticas de uso diário, limites e integridade técnica do motor de correção do TEF Canada Trainer.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiStats ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="p-4 bg-purple-50/40 rounded-2xl border border-purple-100/30 text-center space-y-1">
                  <span className="text-[10px] text-purple-600 font-bold uppercase tracking-wider block">{t("settings.usedToday", "Uso de Hoje")}</span>
                  <p className="text-2xl font-black text-purple-900 font-mono">
                    {aiStats.usedToday} <span className="text-xs text-purple-400 font-bold">/ {aiStats.dailyLimit}</span>
                  </p>
                  <p className="text-[10px] text-purple-500 font-semibold">{t("settings.correctionsTodayMsg", "correções feitas hoje")}</p>
                </div>

                <div className="p-4 bg-emerald-50/40 rounded-2xl border border-emerald-100/30 text-center space-y-1">
                  <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider block">{t("settings.availableToday", "Disponíveis Hoje")}</span>
                  <p className="text-2xl font-black text-emerald-900 font-mono">
                    {aiStats.remaining}
                  </p>
                  <p className="text-[10px] text-emerald-500 font-semibold">{t("settings.remainingMsg", "chamadas restantes")}</p>
                </div>

                <div className="p-4 bg-slate-50/60 rounded-2xl border border-gray-100 text-center space-y-1">
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">{t("settings.totalCorrections", "Total Acumulado")}</span>
                  <p className="text-2xl font-black text-gray-800 font-mono">
                    {aiStats.totalCorrections || 0}
                  </p>
                  <p className="text-[10px] text-gray-500 font-semibold">{t("settings.successfulCorrectionsMsg", "correções bem-sucedidas")}</p>
                </div>

                <div className="p-4 bg-slate-50/60 rounded-2xl border border-gray-100 text-center space-y-1 flex flex-col justify-center">
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">{t("settings.lastCorrection", "Última Correção")}</span>
                  <p className="text-xs font-bold text-gray-700 leading-normal pt-1 font-mono">
                    {aiStats.lastCorrectionAt ? new Date(aiStats.lastCorrectionAt).toLocaleString(i18n.language, {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit"
                    }) : t("settings.noneYet", "Nenhuma ainda")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-5 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200 text-center text-xs text-gray-500 font-bold">
                {t("settings.statsUnavailable", "Estatísticas de uso indisponíveis (servidor offline).")}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer buttons row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 w-full">
          <div className="flex items-center gap-2 text-xs text-gray-400 font-medium w-full sm:w-auto justify-center sm:justify-start">
            <Shield className="w-4 h-4 shrink-0" />
            <span>{t("settings.localStorageNote", "Configurações armazenadas localmente")}</span>
          </div>
          <Button 
            type="submit" 
            className="w-full sm:w-auto bg-primary hover:bg-primary/95 text-white font-bold rounded-xl py-3 px-6 shadow-md flex items-center justify-center gap-2 min-h-[3rem] h-auto whitespace-normal break-words text-center"
          >
            {saved ? (
              <>
                <Check className="w-4 h-4 shrink-0" /> <span>{t("settings.settingsSaved", "Configurações Salvas!")}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 shrink-0" /> <span>{t("settings.saveSettings", "Salvar Configurações")}</span>
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
